import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SeverityBadge } from "@/components/SeverityBadge";
import { Code, Play, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const GRADE_COLOR = { A: "#3ECF8E", B: "#00F0FF", C: "#FFCC00", D: "#FF9500", F: "#FF3B30" };
const SAMPLE = `def login(user, pwd):\n    api_key = "sk_live_51H8xQe2eZvKYlo2C"\n    q = cursor.execute("SELECT * FROM users WHERE name=" + user)\n    import hashlib; hashlib.md5(pwd.encode())\n    eval(request.args.get("cmd"))\n    return q`;

export default function CodeScan() {
  const [filename, setFilename] = useState("app.py");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/codescan/history");
    setHistory(data.scans);
  }, []);
  useEffect(() => { load(); }, [load]);

  const scan = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/codescan", { filename, code });
      setResult(data);
      setHistory((p) => [data, ...p]);
      toast.success(`Scan complete · grade ${data.grade}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Scan failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="codescan-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Static Application Security Testing</p>
        <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Code Scan</h1>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <form onSubmit={scan} className="bg-[#0C0C14] border border-[#1F1F33] p-5 space-y-3">
            <div className="flex items-center gap-3">
              <input data-testid="codescan-filename" value={filename} onChange={(e) => setFilename(e.target.value)}
                className="bg-[#05050A] border border-[#1F1F33] px-3 py-2 text-sm outline-none focus:border-[#00F0FF] w-48" />
              <button type="button" onClick={() => setCode(SAMPLE)} className="text-[10px] uppercase tracking-wider text-[#71717A] hover:text-[#00F0FF]">Load sample</button>
            </div>
            <textarea data-testid="codescan-input" rows={12} value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="Paste source code to scan for hardcoded secrets, SQLi, command injection, weak crypto, XSS sinks…"
              className="w-full bg-black border border-[#1F1F33] px-3 py-2.5 text-xs font-mono outline-none focus:border-[#00F0FF] resize-none placeholder:text-[#71717A]" />
            <button data-testid="run-codescan-button" type="submit" disabled={busy}
              className="flex items-center gap-2 bg-[#00F0FF] text-black font-bold uppercase tracking-[0.15em] text-[11px] px-5 py-2.5 hover:bg-white active:scale-[0.98] transition-all disabled:opacity-50">
              <Play size={14} weight="fill" /> {busy ? "Scanning…" : "Scan Code"}
            </button>
          </form>

          {result && (
            <div className="bg-[#0C0C14] border border-[#1F1F33] p-5 fade-up" data-testid="codescan-result">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 border-2 flex items-center justify-center font-head text-3xl font-black"
                  style={{ borderColor: GRADE_COLOR[result.grade], color: GRADE_COLOR[result.grade] }}>{result.grade}</div>
                <div>
                  <div className="text-sm">{result.filename} · {result.loc} lines</div>
                  <div className="text-[11px] text-[#71717A]">{result.findings.length} issues · {result.severity_count.critical} critical, {result.severity_count.high} high</div>
                </div>
              </div>
              {result.findings.length === 0 ? (
                <div className="text-xs text-[#3ECF8E] flex items-center gap-2"><ShieldCheck size={16} /> No security issues detected.</div>
              ) : (
                <div className="divide-y divide-[#1F1F33] border border-[#1F1F33]">
                  {result.findings.map((f) => (
                    <div key={f.id} className="px-4 py-3" data-testid={`codescan-finding-${f.id}`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <SeverityBadge level={f.severity} />
                        <span className="text-sm">{f.title}</span>
                        <span className="text-[10px] font-mono text-[#00F0FF] border border-[#00F0FF]/40 px-1.5">{f.cwe}</span>
                        <span className="text-[10px] text-[#71717A]">line {f.line}</span>
                      </div>
                      <pre className="bg-black border border-[#1F1F33] mt-2 p-2 text-[11px] text-[#FF9500] overflow-x-auto">{f.snippet}</pre>
                      <p className="text-[11px] text-[#3ECF8E] mt-1 flex items-start gap-1"><ShieldCheck size={12} className="mt-0.5 shrink-0" />{f.recommendation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="xl:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Scan History</div>
          <div className="space-y-2" data-testid="codescan-history">
            {history.map((h) => (
              <button key={h.id} onClick={() => { setResult(h); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="w-full flex items-center gap-3 bg-[#0C0C14] border border-[#1F1F33] px-4 py-3 text-left hover:border-[#33334D]">
                <span className="font-head font-black text-lg" style={{ color: GRADE_COLOR[h.grade] }}>{h.grade}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono truncate">{h.filename}</div>
                  <div className="text-[10px] text-[#71717A]">{h.findings.length} issues · {h.loc} loc</div>
                </div>
              </button>
            ))}
            {history.length === 0 && <div className="text-xs text-[#71717A] py-6 text-center border border-dashed border-[#1F1F33]">No scans yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
